import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { tintaCommittente } from "@/lib/committenti";
import SceltaCommittente from "@/components/SceltaCommittente";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, Plus, Trash2, AlertTriangle, Search, Calendar, Download, FileText, Pencil, Check, X, MapPin, CopyPlus } from "lucide-react";
import { authFetch } from "@/lib/api";
import EquipmentTreePicker from "@/components/EquipmentTreePicker";
import SiteAddressPicker from "@/components/SiteAddressPicker";
import { soloAssunti, nonAncoraAssunto, daQuando, inUfficio,
         bloccatoDaPratica, cosaMancaPratica } from "@/lib/disponibili";
import { dopoLaPersona, etichettaMezzo } from "@/lib/mezziAffidati";
import NomeDaIndirizzo from "@/components/NomeDaIndirizzo";
import { PallineFigure, CoperturaSicurezza } from "@/components/FigureSicurezza";
import AvvisoIndisponibili from "@/components/AvvisoIndisponibili";
import RiallineaSchede from "@/components/RiallineaSchede";

// La griglia parte sempre dal lunedi' e arriva al martedi' della settimana
// dopo: la settimana intera piu' due giorni di anticipo sulla successiva.
const DAYS = 9;
// Avanti e indietro si spostano di una settimana, non di tutta la finestra:
// cosi' ogni pagina riparte dal lunedi' e i due giorni di anticipo si
// ritrovano in testa alla pagina seguente, che e' il punto di quel margine.
// Muovendosi di DAYS le settimane finivano spezzate a meta' su due pagine.
const PASSO = 7;
// Sabato e domenica si stringono: senza cantieri occupano spazio che serve ai
// giorni lavorativi, e servono solo a non perdere il filo del calendario.
const eFestivo = (iso) => [0, 6].includes(new Date(`${iso}T00:00:00`).getDay());

const WEEKDAY = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

function mondayOf(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  return dt.toISOString().substring(0, 10);
}
function fmtDay(iso) {
  const d = new Date(iso);
  return `${WEEKDAY[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
}

function easterMonday(year) {
  // Calcolo Pasquetta (algoritmo di Gauss)
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  const easter = new Date(year, month - 1, day);
  easter.setDate(easter.getDate() + 1);
  return easter;
}
function isItalianHoliday(iso) {
  const d = new Date(iso);
  const md = `${d.getMonth() + 1}-${d.getDate()}`;
  const fixed = ["1-1", "1-6", "4-25", "5-1", "6-2", "8-15", "11-1", "12-8", "12-25", "12-26"];
  if (fixed.includes(md)) return true;
  const em = easterMonday(d.getFullYear());
  return d.getFullYear() === em.getFullYear() && d.getMonth() === em.getMonth() && d.getDate() === em.getDate();
}
function isWeekend(iso) {
  const day = new Date(iso).getDay();
  return day === 0 || day === 6;
}
function shortName(name) {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length < 2) return name || "";
  const surname = parts[0];
  const firstName = parts[parts.length - 1];
  return `${firstName[0].toUpperCase()}.${surname}`;
}

// Come si chiamano le assenze quando si scrivono accanto a un nome. Il server
// l'etichetta la manda gia' scritta: questa resta per le richieste vecchie che
// non ce l'hanno.
const ETICHETTA_ASSENZA = {
  ferie: "ferie", permesso: "permesso", permesso_104: "legge 104",
  malattia: "malattia", altro: "assente",
};

/**
 * Com'e' fatta una giornata: chi e' in squadra, chi resta libero, chi non c'e'.
 *
 * Sta fuori dal componente perche' serve due volte e deve rispondere allo
 * stesso modo: alle pastiglie del giorno selezionato e ai pallini sopra ogni
 * colonna. Quando erano due conti scritti a parte, la pastiglia e il pallino
 * dello stesso giorno potevano dire numeri diversi.
 */
function contaGiornata(giorno, grid, dipendenti, assenze) {
  const impiegati = new Set();
  for (const sq of grid?.squads || []) {
    const cella = grid.entries[`${giorno}|${sq.id}`];
    for (const e of cella?.employees || []) impiegati.add(e.id);
  }

  // Dalle assenze del periodo si tiene quella che tocca questo giorno. Un
  // permesso a ore non toglie la persona: la segnala, perche' mezza giornata
  // in cantiere la fa.
  const assenzaDi = new Map();
  for (const voce of assenze || []) {
    for (const a of voce.assenze || []) {
      if (a.date_from.substring(0, 10) > giorno) continue;
      if (a.date_to.substring(0, 10) < giorno) continue;
      const gia = assenzaDi.get(voce.employee_id);
      // Fra due assenze lo stesso giorno vince quella intera: e' quella che
      // decide se la persona c'e' o no.
      if (!gia || (gia.parziale && !a.parziale)) assenzaDi.set(voce.employee_id, a);
    }
  }

  const liberi = [], mancanti = [];
  for (const d of dipendenti || []) {
    // Chi sta in ufficio non entra in nessuno dei due conti: non e' ne'
    // «da impiegare» ne' «non disponibile», in cantiere non ci va e basta.
    if (inUfficio(d)) continue;
    if (nonAncoraAssunto(d, giorno)) {
      mancanti.push({ ...d, motivo: `entra ${daQuando(d)}` });
      continue;
    }
    // Pratica di assunzione non conclusa: in cantiere non ci puo' andare, e
    // quindi non e' fra quelli che restano da impiegare.
    if (bloccatoDaPratica(d)) {
      mancanti.push({ ...d, motivo: `pratica aperta · manca ${cosaMancaPratica(d)}` });
      continue;
    }
    const a = assenzaDi.get(d.id);
    if (a && !a.parziale) {
      mancanti.push({
        ...d,
        motivo: a.etichetta || ETICHETTA_ASSENZA[a.leave_type] || a.leave_type || "assente",
      });
      continue;
    }
    if (impiegati.has(d.id)) continue;
    liberi.push(a
      ? { ...d, parziale: true,
          motivo: `libero, ma in ${a.etichetta || ETICHETTA_ASSENZA[a.leave_type]
                   || a.leave_type} ${a.ora_inizio}–${a.ora_fine}` }
      : d);
  }

  const perMansione = (x, y) => (x.role || "").localeCompare(y.role || "")
    || (x.full_name || "").localeCompare(y.full_name || "");
  return {
    impiegati: impiegati.size,
    liberi: liberi.sort(perMansione),
    mancanti: mancanti.sort(perMansione),
  };
}

/**
 * Il pallino con il numero sopra una colonna della griglia.
 *
 * La tendina viene portata nel `body` e non dentro la cella: la griglia
 * scorre dentro un riquadro con `overflow: auto`, e un elenco appeso alla
 * cella verrebbe tagliato dal bordo del riquadro invece di uscirne.
 */
function PallinoGiorno({ numero, persone, tinta, titolo, prova }) {
  const [aperta, setAperta] = useState(false);
  const [dove, setDove] = useState(null);
  const bottone = useRef(null);
  const pannello = useRef(null);

  const apri = () => {
    const r = bottone.current?.getBoundingClientRect();
    if (!r) return;
    // L'elenco e' largo 272: se il giorno e' in fondo a destra si appoggia al
    // bordo dello schermo invece di uscirne.
    const meta = Math.min(Math.max(r.left + r.width / 2, 150),
                          window.innerWidth - 150);
    setDove({ sopra: r.bottom + 4, meta });
    setAperta(true);
  };

  useEffect(() => {
    if (!aperta) return;
    const chiudi = () => setAperta(false);
    const fuori = (e) => {
      if (!bottone.current?.contains(e.target)) setAperta(false);
    };
    // Scorrendo la pagina l'elenco resterebbe appeso nel vuoto, perche' e'
    // agganciato allo schermo e non al pallino: quindi si chiude. Ma se a
    // scorrere e' l'elenco stesso - la rotellina sui nomi, quando sono tanti
    // e non ci stanno - non si deve chiudere niente: era il motivo per cui
    // la finestra spariva appena si provava a leggere il fondo dell'elenco.
    const seScorreLaPagina = (e) => {
      if (pannello.current && e.target && pannello.current.contains(e.target)) return;
      chiudi();
    };
    document.addEventListener("mousedown", fuori);
    window.addEventListener("scroll", seScorreLaPagina, true);
    window.addEventListener("resize", chiudi);
    return () => {
      document.removeEventListener("mousedown", fuori);
      window.removeEventListener("scroll", seScorreLaPagina, true);
      window.removeEventListener("resize", chiudi);
    };
  }, [aperta]);

  return (
    <>
      <button type="button" ref={bottone} data-testid={prova}
              title={`${numero} ${titolo.toLowerCase()} · clicca per i nomi`}
              onClick={(e) => {
                e.stopPropagation();
                if (aperta) setAperta(false);
                else apri();
              }}
              className={`w-6 h-6 rounded-full border-2 bg-white text-[11px] font-bold
                          tabular-nums leading-none flex items-center justify-center
                          transition-transform hover:scale-110 ${tinta}`}>
        {numero}
      </button>
      {aperta && dove && createPortal(
        // overscroll-contain: arrivati in fondo ai nomi la rotellina non
        // passa la mano alla pagina, che scorrendo chiuderebbe tutto.
        <div data-testid={`${prova}-elenco`} ref={pannello}
             onMouseDown={(e) => e.stopPropagation()}
             style={{ position: "fixed", top: dove.sopra, left: dove.meta,
                      transform: "translateX(-50%)", zIndex: 60 }}
             className="w-[272px] max-h-72 overflow-y-auto overscroll-contain
                        bg-white border border-slate-200 rounded-lg shadow-xl p-1.5">
          <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400">
            {titolo}
          </div>
          {persone.length === 0 ? (
            <p className="text-xs text-slate-400 px-2 py-2 text-center">Nessuno</p>
          ) : persone.map((p) => (
            <div key={p.id} className="px-2 py-1.5 rounded hover:bg-slate-50 flex items-baseline gap-2">
              <span className="text-sm font-medium min-w-0 truncate">{p.full_name}</span>
              <span className={`text-[11px] min-w-0 truncate ml-auto text-right ${
                p.parziale ? "text-amber-700" : "text-slate-500"}`}>
                {p.motivo || p.role || ""}
              </span>
            </div>
          ))}
        </div>, document.body)}
    </>
  );
}

/**
 * Una tendina con dei nomi: chi resta libero, chi quel giorno non c'e'.
 *
 * Sta chiusa e porta il numero addosso, perche' il numero e' quello che si
 * guarda per primo: «ne restano quattro». Aprendola si leggono i nomi con la
 * mansione, che e' quello che serve subito dopo per decidere chi mandare.
 */
function TendinaPersone({ titolo, persone, tinta, prova }) {
  const [aperta, setAperta] = useState(false);
  const riquadro = useRef(null);

  useEffect(() => {
    if (!aperta) return;
    const fuori = (e) => {
      if (riquadro.current && !riquadro.current.contains(e.target)) setAperta(false);
    };
    document.addEventListener("mousedown", fuori);
    return () => document.removeEventListener("mousedown", fuori);
  }, [aperta]);

  return (
    <div className="relative" ref={riquadro}>
      <button type="button" onClick={() => setAperta((x) => !x)}
              data-testid={prova}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs
                          transition-colors bg-white hover:bg-slate-50 ${tinta}`}>
        <span className="font-semibold tabular-nums">{persone.length}</span>
        {titolo}
        <ChevronDown className={`w-3 h-3 transition-transform ${aperta ? "rotate-180" : ""}`} />
      </button>
      {aperta && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-72 max-h-72 overflow-y-auto
                        bg-white border border-slate-200 rounded-lg shadow-xl p-1.5 z-50"
             data-testid={`${prova}-elenco`}>
          {persone.length === 0 ? (
            <p className="text-xs text-slate-400 px-2 py-2 text-center">Nessuno</p>
          ) : persone.map((p) => (
            <div key={p.id} className="px-2 py-1.5 rounded hover:bg-slate-50 flex items-baseline gap-2">
              <span className="text-sm font-medium min-w-0 truncate">{p.full_name}</span>
              <span className="text-[11px] text-slate-500 min-w-0 truncate ml-auto text-right">
                {p.motivo || p.role || ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PianificazioneGriglia() {
  const [startDate, setStartDate] = useState(mondayOf(new Date()));
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editCell, setEditCell] = useState(null);
  const [duplicaCella, setDuplicaCella] = useState(null);
  const [newSquadName, setNewSquadName] = useState("");
  const [showAddSquad, setShowAddSquad] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  // Chi c'e' in anagrafica e le assenze di tutto il periodo mostrato: servono
  // ai pallini sopra ogni colonna, non piu' al solo giorno selezionato. Una
  // chiamata per periodo invece di una per ogni giorno che si tocca.
  const [tuttiDip, setTuttiDip] = useState([]);
  const [assenzeDelPeriodo, setAssenzeDelPeriodo] = useState([]);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setTuttiDip(data)).catch(() => {});
  }, []);

  // Sabato, domenica e festivi senza nemmeno un cantiere non meritano la stessa
  // larghezza di un giorno di lavoro: si stringono per lasciare spazio agli
  // altri, restando visibili perche' servono a non perdere il filo del
  // calendario. Se invece qualcuno ci lavora tornano larghi come gli altri.
  const strette = useMemo(() => {
    const fuori = new Set();
    for (const d of grid?.dates || []) {
      if (!isWeekend(d) && !isItalianHoliday(d)) continue;
      const occupata = (grid.squads || []).some(sq => grid.entries[`${d}|${sq.id}`]);
      if (!occupata) fuori.add(d);
    }
    return fuori;
  }, [grid]);
  const [draggingKey, setDraggingKey] = useState(null);
  const [mostraGuai, setMostraGuai] = useState(false);

  // Le incongruenze, una per riga invece che un avviso generico: chi, quando,
  // e dove. Su dieci giorni per dodici squadre «qualcuno e' su due cantieri»
  // vuol dire centoventi caselle da guardare a occhio, ed e' il motivo per cui
  // non si trovava.
  const incongruenze = useMemo(() => {
    if (!grid) return [];
    const nome = new Map();       // id -> nome, pescato dalle celle
    const dove = new Map();       // "giorno|id" -> [cantieri]
    const segna = (giorno, id, etichetta, cantiere) => {
      nome.set(id, etichetta);
      const k = `${giorno}|${id}`;
      dove.set(k, [...(dove.get(k) || []), cantiere]);
    };
    for (const d of grid.dates || []) {
      for (const sq of grid.squads || []) {
        const cella = grid.entries[`${d}|${sq.id}`];
        if (!cella) continue;
        const cantiere = `${sq.name} · ${(cella.stops?.length
          ? cella.stops.map((s) => s.site_name).join(" / ")
          : cella.site_name) || "senza cantiere"}`;
        for (const e of cella.employees || []) segna(d, e.id, e.full_name, cantiere);
        for (const q of cella.equipment || []) segna(d, q.id, q.identifier, cantiere);
      }
    }
    const righe = [];
    for (const k of [...(grid.conflict_employees || []), ...(grid.conflict_equipment || [])]) {
      const [giorno, id] = k.split("|");
      righe.push({
        chiave: `doppio-${k}`, tipo: "doppio", giorno,
        chi: nome.get(id) || id,
        cosa: "su più cantieri lo stesso giorno",
        dove: dove.get(k) || [],
      });
    }
    for (const [k, a] of Object.entries(grid.absent_employees || {})) {
      const [giorno, id] = k.split("|");
      righe.push({
        chiave: `assente-${k}`, tipo: "assente", giorno,
        chi: nome.get(id) || id,
        cosa: a.parziale
          ? `assegnato ma in ${a.etichetta} dalle ${a.ora_inizio} alle ${a.ora_fine}`
          : `assegnato ma in ${a.etichetta}`,
        dove: dove.get(k) || [],
      });
    }
    righe.sort((x, y) => x.giorno.localeCompare(y.giorno)
      || x.tipo.localeCompare(y.tipo) || x.chi.localeCompare(y.chi));
    return righe;
  }, [grid]);

  // Portare la griglia sul giorno del guaio: si evidenzia la colonna e la si
  // fa scorrere in vista, che e' quello che si farebbe a mano.
  const vaiAlGiorno = useCallback((giorno) => {
    setSelectedDate(giorno);
    const testa = document.querySelector(`[data-testid="day-header-${giorno}"]`);
    if (testa) testa.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, []);

  // Ogni giorno del periodo diviso in tre: chi e' in squadra, chi non c'e'
  // (ferie, permesso, malattia, non ancora assunto, pratica aperta) e chi
  // resta libero. E' la domanda che ci si fa davanti alla griglia - «chi mi
  // rimane giovedi'?» - e prima bisognava selezionare giovedi' per saperlo.
  const perGiorno = useMemo(() => {
    const fuori = {};
    for (const d of grid?.dates || []) {
      fuori[d] = contaGiornata(d, grid, tuttiDip, assenzeDelPeriodo);
    }
    return fuori;
  }, [grid, tuttiDip, assenzeDelPeriodo]);

  const giornata = (selectedDate && perGiorno[selectedDate]) || null;

  // La legenda dei committenti presenti nel periodo mostrato, con la tinta che
  // gia' si usa sulle barre. Cliccandone uno la griglia tiene solo le squadre
  // che quella settimana lavorano per lui: su dodici righe trovare i propri
  // cantieri a occhio, cercando il colore, e' un lavoro.
  const [filtroCommittenti, setFiltroCommittenti] = useState([]);

  const legenda = useMemo(() => {
    const conteggio = new Map();
    for (const en of Object.values(grid?.entries || {})) {
      const nome = en?.client;
      if (!nome) continue;
      conteggio.set(nome, (conteggio.get(nome) || 0) + 1);
    }
    return Array.from(conteggio.entries())
      .map(([nome, giornate]) => ({ nome, giornate, tinta: tintaCommittente(nome, grid?.colori_committenti) }))
      .sort((a, b) => b.giornate - a.giornate || a.nome.localeCompare(b.nome));
  }, [grid]);

  // Un committente scomparso cambiando periodo non deve restare selezionato di
  // nascosto: la griglia sembrerebbe vuota senza motivo apparente.
  useEffect(() => {
    setFiltroCommittenti(prev => prev.filter(n => legenda.some(l => l.nome === n)));
  }, [legenda]);

  // Una cella "è" del committente se almeno una tappa è di un suo cantiere.
  const cellaDelFiltro = useCallback((en) => {
    if (!en || filtroCommittenti.length === 0) return true;
    const clienti = en.stops?.length ? en.stops.map(s => s.client) : [en.client];
    return clienti.some(c => c && filtroCommittenti.includes(c));
  }, [filtroCommittenti]);

  const squadreVisibili = useMemo(() => {
    const tutte = grid?.squads || [];
    if (filtroCommittenti.length === 0) return tutte;
    return tutte.filter(sq => (grid?.dates || []).some(d => {
      const en = grid.entries[`${d}|${sq.id}`];
      return en && cellaDelFiltro(en);
    }));
  }, [grid, filtroCommittenti, cellaDelFiltro]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/planning/grid?start_date=${startDate}&days=${DAYS}`);
      setGrid(data);
      const ultimo = data.dates?.[data.dates.length - 1] || startDate;
      api.get(`/leave-requests/in-range?date_from=${data.dates?.[0] || startDate}`
              + `&date_to=${ultimo}`)
        .then(({ data: a }) => setAssenzeDelPeriodo(a))
        .catch(() => setAssenzeDelPeriodo([]));
    } catch { toast.error("Errore caricamento griglia"); }
    finally { setLoading(false); }
  }, [startDate]);
  useEffect(() => { load(); }, [load]);

  // Si riparte sempre dal lunedi': mondayOf rimette in riga anche il caso in
  // cui startDate arrivasse da altrove gia' spostato di qualche giorno.
  const shift = (days) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + days);
    setStartDate(mondayOf(d));
  };

  // Si esporta quello che si ha davanti: con la legenda filtrata per
  // committente escono solo le sue celle - le altre restano vuote, perché il
  // foglio va in mano al cliente - e le sole squadre che lavorano per lui.
  const downloadExport = async (format) => {
    try {
      const soloVista = filtroCommittenti.length > 0
        ? `&squad_ids=${encodeURIComponent(squadreVisibili.map(s => s.id).join(","))}`
          + `&committenti=${encodeURIComponent(filtroCommittenti.join("|"))}` : "";
      const r = await authFetch(`/planning/export/${format}?start_date=${startDate}&days=${DAYS}${soloVista}`);
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = `pianificazione_${startDate}.${format}`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore durante l'esportazione"); }
  };

  const nextSquadName = () => {
    const numbers = (grid?.squads || [])
      .map(s => { const m = s.name.match(/(\d+)\s*$/); return m ? parseInt(m[1], 10) : 0; })
      .filter(n => n > 0);
    const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    return `Squadra ${next}`;
  };

  const addSquad = async () => {
    const name = newSquadName.trim() || nextSquadName();
    try {
      await api.post("/planning/squads", { name });
      setNewSquadName(""); setShowAddSquad(false);
      toast.success(`${name} aggiunta`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const removeSquad = async (squad) => {
    if (!window.confirm(`Rimuovere "${squad.name}" dalla griglia? Le assegnazioni passate restano registrate sui cantieri.`)) return;
    try { await api.delete(`/planning/squads/${squad.id}`); toast.success("Squadra rimossa"); load(); }
    catch { toast.error("Errore"); }
  };
  const renameSquad = async (squad, name) => {
    if (!name.trim() || name.trim() === squad.name) return;
    try { await api.put(`/planning/squads/${squad.id}`, { name: name.trim() }); toast.success("Nome squadra aggiornato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nella rinomina"); }
  };

  // La griglia scorre dentro un suo riquadro alto quanto lo schermo che resta:
  // cosi' la riga delle date sta ferma in alto e la barra di scorrimento
  // orizzontale e' sempre in vista, anche con molte squadre.
  const riquadro = useRef(null);
  const [altezzaGriglia, setAltezzaGriglia] = useState(null);
  useLayoutEffect(() => {
    const misura = () => {
      const el = riquadro.current;
      if (!el) return;
      const sopra = el.getBoundingClientRect().top + window.scrollY;
      // sul telefono il menu in basso copre l'ultima parte della pagina
      const sotto = window.innerWidth < 768 ? 120 : 24;
      setAltezzaGriglia(Math.max(360, window.innerHeight - sopra - sotto));
    };
    misura();
    window.addEventListener("resize", misura);
    return () => window.removeEventListener("resize", misura);
  }, [grid, legenda.length, filtroCommittenti.length]);

  const moveEntry = async (fromDate, fromSquadId, toDate, toSquadId, copy) => {
    if (fromDate === toDate && fromSquadId === toSquadId) return;
    try {
      await api.post("/planning/entry/move", {
        from_date: fromDate, from_squad_id: fromSquadId,
        to_date: toDate, to_squad_id: toSquadId, copy,
      });
      toast.success(copy ? "Assegnazione copiata" : "Assegnazione spostata");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nello spostamento"); }
  };

  if (loading && !grid) return <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>;

  return (
    <div className="space-y-6" data-testid="pianificazione-griglia-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Cantieri</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Pianificazione</h1>
          <p className="text-sm text-slate-500 mt-1">Squadre e cantieri, giorno per giorno</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadExport("xlsx")}>
            <Download className="w-4 h-4 mr-2" /> Esporta Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExport("pdf")}>
            <FileText className="w-4 h-4 mr-2" /> Esporta PDF
          </Button>
          <Button variant="outline" onClick={() => { setNewSquadName(nextSquadName()); setShowAddSquad(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Nuova squadra
          </Button>
          <RiallineaSchede dal={startDate}
                           al={grid?.dates?.[grid.dates.length - 1] || startDate}
                           onDone={load} />
        </div>
      </div>

      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" size="sm" onClick={() => shift(-PASSO)}><ChevronLeft className="w-4 h-4" /></Button>
        <span className="font-display font-bold flex items-center gap-2">
          <Calendar className="w-4 h-4 text-orange-500" />
          {grid && `${fmtDay(grid.dates[0])} — ${fmtDay(grid.dates[grid.dates.length - 1])}`}
        </span>
        <Button variant="outline" size="sm" onClick={() => shift(PASSO)}><ChevronRight className="w-4 h-4" /></Button>
        <button type="button" onClick={() => setStartDate(mondayOf(new Date()))} className="text-xs text-orange-600 hover:underline">Oggi</button>
      </div>

      {/* Il trascinamento c'e' da sempre ma non lo sapeva nessuno: era
          scritto solo in una nota del manuale. */}
      <p className="text-[11px] text-slate-500 text-center">
        Trascina una casella per spostarla · <strong>Ctrl + trascina</strong> per
        copiarla con dentro squadra e mezzi · il tasto{" "}
        <CopyPlus className="w-3 h-3 inline-block -mt-0.5" /> sulla casella duplica il
        cantiere a un nuovo indirizzo
      </p>

      {giornata && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500">
            {fmtDay(selectedDate)}: {giornata.impiegati} in squadra
          </span>
          <TendinaPersone titolo="Restano da impiegare" persone={giornata.liberi}
                          tinta="border-[#0067b2] text-[#0067b2]" prova="restano-da-impiegare" />
          <TendinaPersone titolo="Non disponibili" persone={giornata.mancanti}
                          tinta="border-amber-400 text-amber-700" prova="non-disponibili" />
        </div>
      )}

      {legenda.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {legenda.map(({ nome, giornate, tinta }) => {
            const attivo = filtroCommittenti.includes(nome);
            return (
              <button key={nome} type="button" data-testid={`legenda-${nome}`}
                      title={`${giornate} giornate · clicca per vedere solo queste squadre`}
                      onClick={() => setFiltroCommittenti(prev =>
                        prev.includes(nome) ? prev.filter(x => x !== nome) : [...prev, nome])}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs
                                  transition-colors ${attivo
                                    ? "border-slate-900 bg-slate-900 text-white"
                                    : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                <span className="w-3 h-3 rounded-sm flex-shrink-0 border border-black/10"
                      style={{ backgroundColor: `rgba(${tinta} / ${attivo ? 0.95 : 0.6})` }} />
                <span className="truncate max-w-[160px]">{nome}</span>
                <span className={attivo ? "text-slate-300" : "text-slate-400"}>{giornate}</span>
              </button>
            );
          })}
          {filtroCommittenti.length > 0 && (
            <>
              <button type="button" onClick={() => setFiltroCommittenti([])}
                      data-testid="legenda-azzera"
                      className="px-2.5 py-1 rounded-full border border-orange-300 text-orange-700
                                 text-xs hover:bg-orange-50">
                mostra tutte
              </button>
              <span className="text-[11px] text-slate-500 self-center">
                le celle sbiadite sono di altri committenti: nell&apos;esportazione escono vuote
              </span>
            </>
          )}
        </div>
      )}

      {grid && (
        <div ref={riquadro} className="overflow-auto border border-slate-200 rounded-lg"
             style={altezzaGriglia ? { height: altezzaGriglia } : undefined}>
          <table className="w-full table-fixed text-[13px] border-collapse min-w-[860px]">
            <thead>
              {/* I pallini: quanti restano da impiegare (blu) e quanti non
                  sono disponibili (ambra), giorno per giorno. Stanno sopra le
                  date perche' sono la prima cosa che si guarda, e i nomi
                  arrivano col clic. Nei giorni stretti - sabati, domeniche e
                  festivi senza cantieri - non si mettono: non ci si lavora, e
                  due pallini in trentadue pixel non si toccherebbero. */}
              <tr className="bg-white">
                <th className="text-left px-2 sticky left-0 top-0 bg-white z-30 border-r border-slate-200
                               w-24 h-8 text-[10px] font-normal text-slate-400 uppercase tracking-wider">
                  Chi resta
                </th>
                {grid.dates.map(d => {
                  const conto = perGiorno[d];
                  return (
                    // Con `table-fixed` le larghezze le decide la PRIMA riga:
                    // senza ripetere qui w-8 e w-[88px], i sabati tornerebbero
                    // larghi come i giorni di lavoro.
                    <th key={`conti-${d}`}
                        className={`sticky top-0 bg-white z-20 h-8 px-0.5 text-center ${
                          strette.has(d) ? "w-8" : "w-[88px]"}`}>
                      {conto && !strette.has(d) && (
                        <div className="flex items-center justify-center gap-1">
                          <PallinoGiorno numero={conto.liberi.length} persone={conto.liberi}
                                         titolo="Restano da impiegare"
                                         tinta="border-[#0067b2] text-[#0067b2]"
                                         prova={`restano-${d}`} />
                          <PallinoGiorno numero={conto.mancanti.length} persone={conto.mancanti}
                                         titolo="Non disponibili"
                                         tinta="border-amber-400 text-amber-700"
                                         prova={`assenti-${d}`} />
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
              <tr className="bg-slate-50">
                <th className="text-left px-2 py-1.5 sticky left-0 top-8 bg-slate-50 z-20 border-r border-slate-200
                               w-24 shadow-[inset_0_-1px_0_#e2e8f0]">Squadra</th>
                {grid.dates.map(d => {
                  const holiday = isItalianHoliday(d);
                  const weekend = isWeekend(d);
                  const selected = selectedDate === d;
                  // lo sfondo va sulla cella: la riga non segue le celle fissate in alto
                  const cls = selected ? "bg-[#0067b2] text-white"
                    : holiday ? "bg-red-50 text-red-700"
                    : weekend ? "bg-amber-50 text-amber-700" : "bg-slate-50";
                  return (
                    <th key={d} data-testid={`day-header-${d}`}
                        onClick={() => setSelectedDate(prev => prev === d ? null : d)}
                        title="Clicca per evidenziare la giornata"
                        className={`text-center sticky top-8 z-10 shadow-[inset_0_-1px_0_#e2e8f0] text-xs font-semibold cursor-pointer select-none transition-colors ${cls} ${
                          strette.has(d) ? "p-1 w-8" : "px-1 py-1.5 w-[88px]"}`}>
                      {strette.has(d)
                        ? <span className="text-[10px] leading-none" title={fmtDay(d)}>
                            {fmtDay(d).slice(0, 3)}
                          </span>
                        : <>{fmtDay(d)}{holiday && <div className="text-[9px] font-normal">festivo</div>}</>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grid.squads.length === 0 && (
                <tr><td colSpan={DAYS + 1} className="text-center p-8 text-slate-400">Nessuna squadra — creane una per iniziare.</td></tr>
              )}
              {grid.squads.length > 0 && squadreVisibili.length === 0 && (
                <tr><td colSpan={DAYS + 1} className="text-center p-8 text-slate-400">
                  Nessuna squadra lavora per il committente scelto in queste giornate.
                </td></tr>
              )}
              {squadreVisibili.map(sq => (
                <tr key={sq.id}>
                  <td className="px-2 py-1 sticky left-0 bg-white z-10 border-r border-b border-slate-100 font-semibold">
                    <SquadNameCell squad={sq} onRename={renameSquad} onRemove={removeSquad} />
                  </td>
                  {grid.dates.map(d => {
                    const key = `${d}|${sq.id}`;
                    const entry = grid.entries[key];
                    const holiday = isItalianHoliday(d);
                    const weekend = isWeekend(d);
                    const isSelectedDay = selectedDate === d;
                    const cellBg = isSelectedDay ? "bg-blue-50"
                      : holiday ? "bg-red-50/50" : weekend ? "bg-amber-50/50" : "";
                    const cellKey = `${d}|${sq.id}`;
                    const isDragOver = dragOverKey === cellKey;
                    const isDragging = draggingKey === cellKey;
                    // La barra sotto e' continua: si arrotonda solo dove il
                    // cantiere comincia e dove finisce, cosi' i giorni
                    // attaccati sembrano un pezzo unico invece di tanti
                    // rettangoli affiancati.
                    const iGiorno = grid.dates.indexOf(d);
                    const vicino = (passo) => {
                      const altro = grid.dates[iGiorno + passo];
                      return altro ? grid.entries[`${altro}|${sq.id}`] : null;
                    };
                    const stessoCantiere = (e) => e && entry && e.site_id === entry.site_id;
                    const attaccatoPrima = stessoCantiere(vicino(-1));
                    const attaccatoDopo = stessoCantiere(vicino(1));
                    const tinta = tintaCommittente(entry?.client, grid?.colori_committenti);
                    return (
                      <td key={d}
                          style={tinta && !isSelectedDay
                            ? { backgroundColor: `rgba(${tinta} / 0.16)` } : undefined}
                          className={`group relative border-b align-top ${cellBg} ${
                            strette.has(d) ? "p-0 w-8" : "p-0.5"} ${
                            isSelectedDay ? "border-l-2 border-r-2 border-l-[#0067b2] border-r-[#0067b2]" : "border-l border-slate-100"
                          } ${isDragOver ? "ring-2 ring-orange-400 ring-inset" : ""}`}
                          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = e.ctrlKey ? "copy" : "move"; setDragOverKey(cellKey); }}
                          onDragLeave={() => setDragOverKey(prev => prev === cellKey ? null : prev)}
                          onDrop={async (e) => {
                            e.preventDefault();
                            setDragOverKey(null);
                            const raw = e.dataTransfer.getData("text/plain");
                            if (!raw) return;
                            const { date: fromDate, squadId: fromSquadId } = JSON.parse(raw);
                            if (fromDate === d && fromSquadId === sq.id) return;
                            const copy = e.ctrlKey;
                            if (entry && !window.confirm(`Questa cella contiene già "${entry.site_name}". Sovrascrivere?`)) return;
                            await moveEntry(fromDate, fromSquadId, d, sq.id, copy);
                          }}>
                        <button type="button" onClick={() => setEditCell({ date: d, squad: sq, entry })}
                                draggable={!!entry}
                                onDragStart={(e) => {
                                  if (!entry) return;
                                  e.dataTransfer.setData("text/plain", JSON.stringify({ date: d, squadId: sq.id }));
                                  e.dataTransfer.effectAllowed = "copyMove";
                                  setDraggingKey(cellKey);
                                }}
                                onDragEnd={() => setDraggingKey(null)}
                                title={entry && !cellaDelFiltro(entry) ? "Di un altro committente: non esce nell'esportazione filtrata" : undefined}
                                className={`w-full h-full min-h-[58px] text-left p-1 pb-2.5 rounded hover:bg-orange-50 transition ${entry ? "cursor-grab active:cursor-grabbing" : ""} ${isDragging ? "opacity-40" : ""} ${entry && !cellaDelFiltro(entry) ? "opacity-30 grayscale" : ""}`}>
                          {entry ? (
                            <div>
                              {(entry.stops?.length ? entry.stops : [{ site_id: entry.site_id, site_name: entry.site_name }])
                                .map((s, i) => (
                                  <div key={`${s.site_id}-${i}`}
                                       className={`text-[11px] leading-tight font-semibold truncate ${i > 0 ? "border-t border-dashed border-slate-200 pt-0.5 mt-0.5" : ""}`}>
                                    {entry.stops?.length > 1 && (
                                      <MapPin className="w-2.5 h-2.5 inline-block mr-0.5 text-[#0067b2]" />
                                    )}
                                    {s.site_name}
                                    {s.note && <span className="font-normal text-slate-400"> · {s.note}</span>}
                                  </div>
                                ))}
                              <div className="flex flex-wrap gap-0.5 mb-0.5 mt-0.5">
                                {entry.employees.map(e => {
                                  const conflict = grid.conflict_employees.includes(`${d}|${e.id}`);
                                  const assente = grid.absent_employees?.[`${d}|${e.id}`];
                                  const tinta = conflict || (assente && !assente.parziale)
                                    ? "bg-red-100 text-red-700 ring-1 ring-red-400"
                                    : assente ? "bg-amber-100 text-amber-800 ring-1 ring-amber-400"
                                    : "bg-orange-100 text-orange-700";
                                  const titolo = assente
                                    ? `${e.full_name} · ${assente.etichetta}${assente.parziale ? ` ${assente.ora_inizio}–${assente.ora_fine}` : ""}`
                                    : e.full_name;
                                  return (
                                    <span key={e.id} title={titolo}
                                          className={`px-1.5 h-4 rounded text-[9px] flex items-center justify-center flex-shrink-0 whitespace-nowrap ${tinta}`}>
                                      {shortName(e.full_name)}{assente && !assente.parziale ? " ✕" : ""}
                                    </span>
                                  );
                                })}
                              </div>
                              {entry.equipment.length > 0 && (
                                <div className="text-[9px] text-slate-500 truncate">{entry.equipment.map(e => e.identifier).join(", ")}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-300">
                              {strette.has(d) ? "" : "+ assegna"}
                            </span>
                          )}
                        </button>
                        {/* Il gesto del foglio Excel: copia la giornata su un
                            altro indirizzo. Compare passando sopra la cella,
                            per non rubare spazio a quello che c'e' scritto. */}
                        {entry && !strette.has(d) && (
                          <button type="button" title="Duplica con nuovo indirizzo"
                                  data-testid={`duplica-${d}-${sq.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDuplicaCella({ date: d, squad: sq, entry });
                                  }}
                                  className="absolute top-0.5 right-0.5 opacity-0 hover:opacity-100
                                             focus:opacity-100 group-hover:opacity-100 transition
                                             bg-white/90 border border-slate-200 rounded p-0.5
                                             text-slate-500 hover:text-[#0067b2]">
                            <CopyPlus className="w-3 h-3" />
                          </button>
                        )}
                        {entry && (
                          /* Sta sulla cella, non dentro il pulsante: cosi'
                             attraversa i margini e sconfina di un pixel sul
                             bordo, e i giorni attaccati diventano una barra
                             sola. Si interrompe solo quando cambia cantiere. */
                          <div className={`absolute bottom-0 -left-px -right-px h-2 pointer-events-none ${
                                attaccatoPrima ? "" : "rounded-l-full"} ${
                                attaccatoDopo ? "" : "rounded-r-full"}`}
                               style={{ backgroundColor: tinta
                                 ? `rgba(${tinta} / 0.75)` : "rgba(148 163 184 / 0.6)" }}
                               title={entry.client || entry.site_name} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {incongruenze.length > 0 && (
        <div className="border border-red-200 bg-red-50 rounded-lg" data-testid="incongruenze">
          <button type="button" onClick={() => setMostraGuai((x) => !x)}
                  data-testid="vedi-incongruenze"
                  className="w-full flex items-center gap-2 text-sm text-red-700 px-3 py-2 text-left">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="font-semibold">{incongruenze.length}</span>
            {incongruenze.length === 1 ? "incongruenza in queste giornate" : "incongruenze in queste giornate"}
            <span className="ml-auto text-xs underline">
              {mostraGuai ? "nascondi" : "vedi incongruenze"}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${mostraGuai ? "rotate-180" : ""}`} />
          </button>
          {mostraGuai && (
            <div className="px-2 pb-2 space-y-1" data-testid="elenco-incongruenze">
              {incongruenze.map((g) => (
                <button key={g.chiave} type="button" onClick={() => vaiAlGiorno(g.giorno)}
                        className="w-full text-left bg-white border border-red-200 rounded px-2.5 py-1.5
                                   hover:border-red-400 transition-colors">
                  <div className="text-sm flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold">{g.chi}</span>
                    <span className="text-red-700">{g.cosa}</span>
                    <span className="ml-auto text-xs text-slate-500">{fmtDay(g.giorno)}</span>
                  </div>
                  {g.dove.length > 0 && (
                    <div className="text-xs text-slate-500 truncate">{g.dove.join("  +  ")}</div>
                  )}
                </button>
              ))}
              <p className="text-[11px] text-slate-500 px-1 pt-1">
                Tocca una riga per andare a quel giorno: la colonna si evidenzia in griglia.
              </p>
            </div>
          )}
        </div>
      )}

      {duplicaCella && (
        <DuplicaCantiereDialog cella={duplicaCella}
                               onClose={() => setDuplicaCella(null)}
                               onFatto={() => { setDuplicaCella(null); load(); }} />
      )}
      {editCell && (
        <EntryEditDialog cell={editCell} onClose={() => setEditCell(null)}
                         onSaved={() => { setEditCell(null); load(); }} />
      )}

      {showAddSquad && (
        <Dialog open onOpenChange={() => setShowAddSquad(false)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuova squadra</DialogTitle></DialogHeader>
            <Input placeholder="Nome squadra (es. Squadra 3)" value={newSquadName}
                   onChange={(e) => setNewSquadName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSquad()} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddSquad(false)}>Annulla</Button>
              <Button onClick={addSquad} className="bg-orange-500 hover:bg-orange-600 text-white">Aggiungi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SquadNameCell({ squad, onRename, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(squad.name);

  if (editing) {
    const confirm = () => { onRename(squad, name); setEditing(false); };
    const cancel = () => { setName(squad.name); setEditing(false); };
    return (
      <div className="flex items-center gap-1">
        <Input autoFocus value={name} onChange={(e) => setName(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") confirm(); if (e.key === "Escape") cancel(); }}
               className="h-7 text-xs px-1.5" data-testid={`squad-name-input-${squad.id}`} />
        <button type="button" onClick={confirm} className="text-emerald-600 flex-shrink-0"><Check className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={cancel} className="text-slate-400 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-1 group/sq">
      <button type="button" onClick={() => { setName(squad.name); setEditing(true); }}
              title="Rinomina squadra" data-testid={`squad-rename-${squad.id}`}
              className="truncate text-left hover:text-orange-600 inline-flex items-center gap-1 min-w-0">
        <span className="truncate">{squad.name}</span>
        <Pencil className="w-2.5 h-2.5 opacity-0 group-hover/sq:opacity-100 flex-shrink-0" />
      </button>
      <button type="button" onClick={() => onRemove(squad)} className="text-slate-300 hover:text-red-500 flex-shrink-0">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function EntryEditDialog({ cell, onClose, onSaved }) {
  const { date, squad, entry } = cell;
  const initialStops = entry?.stops?.length
    ? entry.stops.map(s => ({ site_id: s.site_id, note: s.note || "" }))
    : (entry?.site_id ? [{ site_id: entry.site_id, note: "" }] : [{ site_id: "", note: "" }]);

  const [sites, setSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [onLeave, setOnLeave] = useState([]);
  const [stops, setStops] = useState(initialStops);
  const [multi, setMulti] = useState(initialStops.length > 1);
  const [selEmp, setSelEmp] = useState((entry?.employees || []).map(e => e.id));
  const [selEq, setSelEq] = useState((entry?.equipment || []).map(e => e.id));
  const [empSearch, setEmpSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [showNewSite, setShowNewSite] = useState(false);
  const [newSiteFor, setNewSiteFor] = useState(0);
  const skipAutofill = React.useRef(!!entry?.site_id);

  useEffect(() => {
    api.get("/sites").then(({ data }) => setSites(data)).catch(() => {});
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
    api.get("/equipment").then(({ data }) => setEquipment(data)).catch(() => {});
    api.get(`/leave-requests/active-on?date=${date}`).then(({ data }) => setOnLeave(data)).catch(() => {});
  }, [date]);

  // Scegliendo il cantiere principale su una cella vuota si eredita la squadra
  // gia' assegnata a quel cantiere, cosi' non va ricomposta a mano.
  const firstSite = stops[0]?.site_id;
  useEffect(() => {
    if (!firstSite) return;
    if (skipAutofill.current) { skipAutofill.current = false; return; }
    api.get(`/sites/${firstSite}`).then(({ data }) => {
      setSelEmp((data.employees || []).map(e => e.id));
      setSelEq((data.equipment || []).map(e => e.id));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSite]);

  const setStop = (i, patch) => setStops(prev => prev.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const addStop = () => setStops(prev => [...prev, { site_id: "", note: "" }]);
  const removeStop = (i) => setStops(prev => (prev.length === 1 ? prev : prev.filter((_, k) => k !== i)));

  const onSiteCreated = (site) => {
    // In cima: e' quello che si e' appena creato e che si sta per scegliere.
    setSites(prev => [site, ...prev]);
    skipAutofill.current = true;
    setStop(newSiteFor, { site_id: site.id });
    setShowNewSite(false);
  };

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  // Spuntando una persona arriva anche il mezzo che le e' affidato - quello
  // con il suo nome come utilizzatore, lo stesso che si vede sulla
  // localizzazione - e togliendola se ne va con lei. `mezziAuto` ricorda
  // quali sono arrivati cosi': un mezzo aggiunto a mano resta dov'e'.
  const [mezziAuto, setMezziAuto] = useState([]);

  const tocca = (empId) => {
    const entra = !selEmp.includes(empId);
    const personeDopo = entra ? [...selEmp, empId] : selEmp.filter((x) => x !== empId);
    setSelEmp(personeDopo);
    const esito = dopoLaPersona({
      id: empId, entra, personeDopo, mezzi: selEq, auto: mezziAuto, equipment,
    });
    setSelEq(esito.mezzi);
    setMezziAuto(esito.auto);
    const nome = (employees.find((x) => x.id === empId) || {}).full_name || "";
    if (esito.aggiunti?.length) {
      toast.message("Arriva anche il mezzo", {
        description: esito.aggiunti
          .map((m) => etichettaMezzo(equipment.find((q) => q.id === m)))
          .join(", ") + ` · affidato a ${nome}`,
      });
    }
    if (esito.fermi?.length) {
      toast.warning(`${etichettaMezzo(esito.fermi[0])} è fermo`, {
        description: `È il mezzo di ${nome}: scegline un altro a mano.`,
      });
    }
  };



  // Per quali giorni vale la modifica. Si chiede prima di scrivere: sostituire
  // una persona per una giornata sola non deve riscrivere il resto del
  // cantiere, ed e' proprio quello che succedeva quando l'unica strada era
  // stendere fino alla fine.
  const [ambito, setAmbito] = useState("fine_cantiere");
  const [periodoDal, setPeriodoDal] = useState(date);
  const [periodoAl, setPeriodoAl] = useState(date);

  const save = async () => {
    const chosen = stops.filter(s => s.site_id);
    if (chosen.length === 0) { toast.error("Seleziona un cantiere"); return; }
    setBusy(true);
    try {
      if (chosen.length === 1) {
        // Un cantiere solo: si incolla su tutto il suo periodo, scivolando di
        // riga se la squadra scelta e' gia' occupata in uno di quei giorni.
        const { data } = await api.post("/planning/entry/stendi", {
          date, squad_id: squad.id, site_id: chosen[0].site_id,
          employee_ids: selEmp, equipment_ids: selEq,
          ambito,
          dal: ambito === "periodo" ? periodoDal : null,
          al: ambito === "periodo" ? periodoAl : null,
        });
        toast.success(data.spostato
          ? `Assegnato a ${data.squadra} (${data.giorni} giorni): la squadra scelta era occupata`
          : `Assegnato per ${data.giorni} giorn${data.giorni === 1 ? "o" : "i"}`);
      } else {
        // Giornata spezzata su piu' indirizzi: riguarda solo quel giorno.
        await api.put("/planning/entry", {
          date, squad_id: squad.id,
          site_id: chosen[0].site_id,
          stops: chosen.map(s => ({ site_id: s.site_id, note: s.note?.trim() || null })),
          employee_ids: selEmp, equipment_ids: selEq,
        });
        toast.success("Assegnazione salvata");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try {
      await api.put("/planning/entry", { date, squad_id: squad.id, site_id: null, stops: [], employee_ids: [], equipment_ids: [] });
      toast.success("Cella svuotata");
      onSaved();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  // Chi comincia dopo questa giornata non si puo' assegnare: per la giornata
  // in cui comincia invece si, ed e' per questo che si guarda la data della
  // casella e non quella di oggi.
  const filteredEmp = soloAssunti(employees, date)
    .filter(e => !empSearch || e.full_name.toLowerCase().includes(empSearch.toLowerCase()));
  const visibleStops = multi ? stops : stops.slice(0, 1);
  // I cantieri terminati restano visibili sulla griglia ma non si ripropongono
  // fra quelli assegnabili, salvo che siano gia' su questa cella.
  const chosenIds = stops.map(s => s.site_id);
  const selectableSites = sites.filter(s => s.status !== "chiuso" || chosenIds.includes(s.id));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{squad.name} · {fmtDay(date)}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label>Cantiere</Label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={multi} data-testid="multi-stop-toggle"
                       onChange={(e) => {
                         setMulti(e.target.checked);
                         if (e.target.checked && stops.length === 1) addStop();
                         if (!e.target.checked) setStops(prev => prev.slice(0, 1));
                       }} />
                Giornata su più indirizzi
              </label>
            </div>
            <div className="space-y-2 mt-1">
              {visibleStops.map((s, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-1 min-w-0">
                    <select className="w-full h-10 border border-slate-200 rounded px-3" value={s.site_id}
                            data-testid={`stop-site-${i}`}
                            onChange={(e) => {
                              if (e.target.value === "__new__") { setNewSiteFor(i); setShowNewSite(true); }
                              else setStop(i, { site_id: e.target.value });
                            }}>
                      <option value="">{i === 0 ? "Nessuno / libera" : "Seconda tappa..."}</option>
                      {/* In cima: con quaranta cantieri in elenco, in fondo non lo trovava nessuno. */}
                      <option value="__new__">+ Crea nuovo cantiere...</option>
                      {selectableSites.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  </div>
                  {multi && (
                    <>
                      <Input className="w-36 h-10" placeholder="es. mattina" value={s.note}
                             onChange={(e) => setStop(i, { note: e.target.value })} />
                      <button type="button" onClick={() => removeStop(i)}
                              className="h-10 px-2 text-slate-400 hover:text-red-500 flex-shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {multi && (
                <Button type="button" variant="outline" size="sm" onClick={addStop} data-testid="add-stop-btn">
                  <Plus className="w-3 h-3 mr-1" /> Aggiungi indirizzo
                </Button>
              )}
            </div>
            {multi && (
              <p className="text-[10px] text-slate-500 mt-1">
                La giornata viene divisa fra gli indirizzi indicati: personale e mezzi restano gli stessi,
                usa la nota per segnare la fascia oraria di ciascuna tappa.
              </p>
            )}
          </div>
          {/* Chi c'e' gia': scelto il cantiere, la squadra si eredita da sola, e
              qui si vede a colpo d'occhio chi e cosa e' selezionato senza
              scorrere le caselle. La x toglie. */}
          {(selEmp.length > 0 || selEq.length > 0) && (
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2"
                 data-testid="riepilogo-selezione">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Già in cantiere · {selEmp.length} {selEmp.length === 1 ? "persona" : "persone"}
                {selEq.length > 0 && ` · ${selEq.length} ${selEq.length === 1 ? "mezzo" : "mezzi"}`}
              </div>
              <div className="flex flex-wrap gap-1">
                {selEmp.map(id => {
                  const e = employees.find(x => x.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 rounded-full pl-2 pr-1 py-0.5">
                      {e?.full_name || id}
                      <button type="button" title="Togli" onClick={() => tocca(id)}
                              className="text-slate-400 hover:text-red-500 leading-none px-0.5">×</button>
                    </span>
                  );
                })}
                {selEq.map(id => {
                  const m = equipment.find(x => x.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-blue-50 border border-blue-200 text-blue-800 rounded-full pl-2 pr-1 py-0.5">
                      {m ? `${m.identifier}${m.model ? ` · ${m.model}` : ""}` : id}
                      <button type="button" title="Togli" onClick={() => toggle(selEq, setSelEq, id)}
                              className="text-blue-400 hover:text-red-500 leading-none px-0.5">×</button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <AvvisoIndisponibili contesto="in questa giornata"
            assenti={selEmp.map(id => {
              const l = onLeave.find(x => x.employee_id === id && !x.parziale);
              if (!l) return null;
              const e = employees.find(x => x.id === id);
              return { id, full_name: e?.full_name || l.employee_name, etichetta: l.leave_type };
            }).filter(Boolean)}
            parziali={selEmp.map(id => {
              const l = onLeave.find(x => x.employee_id === id && x.parziale);
              if (!l) return null;
              const e = employees.find(x => x.id === id);
              return { id, full_name: e?.full_name || l.employee_name, etichetta: l.leave_type,
                       ora_inizio: l.ora_inizio, ora_fine: l.ora_fine };
            }).filter(Boolean)} />

          <CoperturaSicurezza
            dipendentiSelezionati={employees.filter(e => selEmp.includes(e.id))} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Personale</Label>
              <div className="relative mt-1 mb-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <Input className="pl-7 h-9 text-sm" value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} placeholder="Cerca..." />
              </div>
              <div className="border border-slate-200 rounded max-h-52 overflow-y-auto divide-y divide-slate-100">
                {filteredEmp.map(e => {
                  const leave = onLeave.find(l => l.employee_id === e.id);
                  // Chi ha la pratica di assunzione aperta non si spunta: in
                  // cantiere non ci va, e il server lo rifiuterebbe comunque.
                  const bloccato = bloccatoDaPratica(e) && !selEmp.includes(e.id);
                  return (
                    <label key={e.id} title={bloccato
                             ? `Pratica di assunzione da concludere · manca ${cosaMancaPratica(e)}`
                             : undefined}
                           className={`flex items-center gap-2 p-2 text-sm ${
                             bloccato ? "bg-red-50/60 text-slate-400 cursor-not-allowed"
                                      : "hover:bg-slate-50 cursor-pointer"} ${
                             leave ? (leave.parziale ? "bg-amber-50" : "bg-red-50") : ""}`}>
                      <input type="checkbox" checked={selEmp.includes(e.id)} disabled={bloccato}
                             onChange={() => tocca(e.id)} />
                      <span className="truncate flex-1">{e.full_name}</span>
                      {bloccato && (
                        <span className="text-[9px] font-semibold text-red-600 flex-shrink-0">
                          pratica aperta
                        </span>
                      )}
                      <PallineFigure figure={e.figure_sicurezza} />
                      {e.is_preposto && (
                        <span className="text-[9px] text-[#0067b2] bg-blue-100 rounded px-1 flex-shrink-0">preposto</span>
                      )}
                      {leave && (
                        <span className={`text-[9px] font-semibold flex-shrink-0 ${
                          leave.parziale ? "text-amber-700" : "text-red-600"}`}>
                          {leave.parziale
                            ? `${leave.ora_inizio}–${leave.ora_fine}`
                            : `in ${leave.leave_type}`}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Mezzi</Label>
              <EquipmentTreePicker equipment={equipment} selected={selEq}
                                   onToggle={(eid) => toggle(selEq, setSelEq, eid)} />
            </div>
          </div>
        </div>

        {/* La domanda vale solo per il cantiere singolo: una giornata spezzata
            su piu' indirizzi riguarda per sua natura quel giorno soltanto. */}
        {stops.filter(x => x.site_id).length === 1 && (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
            <Label className="text-sm">Per quali giorni vale questa modifica</Label>
            <div className="flex gap-2 flex-wrap">
              {[["giorno", `Solo ${fmtDay(date)}`],
                ["fine_cantiere", "Fino a fine cantiere"],
                ["periodo", "Fra due date"]].map(([v, t]) => (
                <button key={v} type="button" data-testid={`ambito-${v}`}
                        onClick={() => setAmbito(v)}
                        className={`text-sm px-3 py-2 rounded-full border transition-colors ${
                          ambito === v ? "bg-orange-500 border-orange-500 text-white"
                                       : "border-slate-200 text-slate-600 hover:bg-white"}`}>
                  {t}
                </button>
              ))}
            </div>
            {ambito === "periodo" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Dal</Label>
                  <Input type="date" className="h-10 mt-1" value={periodoDal}
                         onChange={(e) => setPeriodoDal(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Al</Label>
                  <Input type="date" className="h-10 mt-1" value={periodoAl}
                         onChange={(e) => setPeriodoAl(e.target.value)} />
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-500">
              {ambito === "giorno"
                ? "Gli altri giorni del cantiere restano come sono."
                : ambito === "periodo"
                  ? "Fuori da queste date non viene toccato niente."
                  : "Da questo giorno in avanti tutte le giornate prendono queste persone e questi mezzi."}
              {" "}I giorni già passati non si toccano mai.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {entry && <Button variant="outline" onClick={clear} disabled={busy} className="text-red-600">Svuota cella</Button>}
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">Salva</Button>
        </DialogFooter>
      </DialogContent>
      {showNewSite && <QuickSiteDialog date={date} onClose={() => setShowNewSite(false)} onCreated={onSiteCreated} />}
    </Dialog>
  );
}

/**
 * Creazione cantiere dalla pianificazione con gli stessi campi della gestione
 * cantieri: solo il nome e' obbligatorio, il resto lo completa il tecnico di
 * cantiere quando ha i dati.
 */
/**
 * Duplica il cantiere della cella a un indirizzo nuovo.
 *
 * Per le squadre che girano tutti i giorni fra un cantiere e l'altro cambiano
 * solo tre cose - indirizzo, numero di commessa, date - e tutto il resto e'
 * identico: committente, tipo di attivita', squadra, persone, mezzi. Finora
 * per ognuno si ricompilava un cantiere da sei campi; sul foglio Excel si
 * copiava la cella e si correggeva l'indirizzo, e questo e' quel gesto.
 */
function DuplicaCantiereDialog({ cella, onClose, onFatto }) {
  const { date, squad, entry } = cella;
  const domani = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };
  const [f, setF] = useState({
    address: "", name: "", numero_commessa: "",
    dal: domani(date), al: domani(date),
    comune: "", provincia: "", cap: "", lat: null, lng: null,
  });
  const [copiaPos, setCopiaPos] = useState(true);
  const [busy, setBusy] = useState(false);

  const duplica = async () => {
    if (!f.address.trim()) { toast.error("Indica l'indirizzo del cantiere nuovo"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/planning/entry/duplica", {
        from_date: date, from_squad_id: squad.id,
        ...f, al: f.al || f.dal,
        name: f.name.trim() || undefined,
        copia_pos: copiaPos,
      });
      const quante = data.giornate.length;
      toast.success(`${data.cantiere.name} · ${quante} giornat${quante === 1 ? "a" : "e"}`
        + ` con ${data.persone} in squadra`);
      if (data.sovrascritte?.length) {
        toast.warning(`Sostituito quello che c'era: `
          + data.sovrascritte.map((s) => `${fmtDay(s.date)} ${s.site_name}`).join(", "));
      }
      onFatto();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella duplicazione");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"
                     onPointerDownOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }}
                     onInteractOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }}
                     onFocusOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Duplica con nuovo indirizzo</DialogTitle>
        </DialogHeader>

        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Si ricalca <strong>{entry?.site_name}</strong> di {squad.name}: restano lo
          stesso committente, lo stesso tipo di attività, le stesse{" "}
          {entry?.employees?.length || 0} persone e gli stessi{" "}
          {entry?.equipment?.length || 0} mezzi. Cambiano solo le tre cose qui sotto.
        </div>

        <div className="space-y-3">
          <SiteAddressPicker value={f} onChange={(patch) => setF(prev => ({ ...prev, ...patch }))} />
          <div>
            <Label>Nome cantiere</Label>
            <div className="flex gap-2">
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                     placeholder="se lo lasci vuoto prende l'indirizzo"
                     data-testid="duplica-nome" />
              <NomeDaIndirizzo scheda={f} onNome={(nome) => setF((p) => ({ ...p, name: nome }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Numero commessa</Label>
              <Input value={f.numero_commessa} data-testid="duplica-commessa"
                     onChange={(e) => setF({ ...f, numero_commessa: e.target.value })} />
            </div>
            <div>
              <Label>Dal</Label>
              <Input type="date" value={f.dal} data-testid="duplica-dal"
                     onChange={(e) => setF({ ...f, dal: e.target.value,
                                             al: f.al < e.target.value ? e.target.value : f.al })} />
            </div>
            <div>
              <Label>Al</Label>
              <Input type="date" value={f.al} data-testid="duplica-al"
                     onChange={(e) => setF({ ...f, al: e.target.value })} />
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-600">
            <input type="checkbox" className="mt-0.5" checked={copiaPos}
                   onChange={(e) => setCopiaPos(e.target.checked)} />
            <span>
              Porta anche il POS già compilato di {entry?.site_name}
              <span className="block text-[11px] text-slate-500">
                Stesso lavoro, stesse risposte: resta tutto modificabile, e il
                documento si rigenera quando serve.
              </span>
            </span>
          </label>
          <p className="text-[11px] text-slate-500">
            Le giornate dal/al si riempiono da sole sulla riga di {squad.name}, saltando
            il fine settimana se il cantiere di partenza lo salta.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={duplica} disabled={busy || !f.address.trim()}
                  data-testid="conferma-duplica"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            <CopyPlus className="w-4 h-4 mr-2" />
            {busy ? "Creo il cantiere..." : "Duplica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuickSiteDialog({ date, onClose, onCreated }) {
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  // Si parte dal giorno della casella da cui si e' arrivati: e' quello il
  // giorno in cui il cantiere comincia, altrimenti non lo si starebbe
  // assegnando li'.
  const [f, setF] = useState({
    name: "", address: "", client: "", client_id: null, activity_type: "",
    start_date: date || "", end_date_planned: "", notes: "", status: "attivo",
  });

  useEffect(() => {
    api.get("/sites/activity-types").then(({ data }) => setTypes(data)).catch(() => {});
  }, []);

  const create = async () => {
    if (!f.name.trim()) { toast.error("Indica almeno il nome del cantiere"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sites", { ...f, name: f.name.trim(), address: f.address || "" });
      toast.success("Cantiere creato");
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella creazione"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"
                     onPointerDownOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }}
                     onInteractOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }}
                     onFocusOutside={(e) => { if (e.target.closest(".pac-container")) e.preventDefault(); }}>
        <DialogHeader><DialogTitle>Nuovo cantiere</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <SiteAddressPicker value={f} onChange={(patch) => setF(prev => ({ ...prev, ...patch }))} />
          <div><Label>Nome cantiere *</Label>
            <div className="flex gap-2">
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                     data-testid="quick-site-name" />
              <NomeDaIndirizzo scheda={f} onNome={(nome) => setF((p) => ({ ...p, name: nome }))} />
            </div>
          </div>
          <SceltaCommittente value={f.client_id} testo={f.client}
                             onChange={(id) => setF({ ...f, client_id: id })} />
          <div><Label>Tipo attività</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.activity_type} onChange={(e) => setF({ ...f, activity_type: e.target.value })}>
              <option value="">Da definire</option>
              {types.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data inizio prevista</Label>
              <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
            <div><Label>Data fine prevista</Label>
              <Input type="date" value={f.end_date_planned} onChange={(e) => setF({ ...f, end_date_planned: e.target.value })} /></div>
          </div>
          <div><Label>Note</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <p className="text-[10px] text-slate-500">
            I campi lasciati vuoti si completano in un secondo momento dalla scheda cantiere.
            Indicando entrambe le date il cantiere viene riportato in automatico sulla griglia.
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          <Button type="button" onClick={create} disabled={busy}
                  className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="quick-site-create">
            {busy ? "Creazione..." : "Crea e assegna"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
